import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Checkbox, FormControlLabel, Grid, TextField } from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { FooterModalButtons } from "../../../../../controls/buttons/FooterModalButtons";
import { ConfirmModal } from "../../../../../controls/modals/ConfirmModal";
import { NotificationSnackBar } from "../../../../../controls/snackbars/notification-snackbar";
import { MoldSizeAutocomplete } from "../../../../../shared/autocompletes/MoldSizeAutocomplete";
import {
  CATALOG_MOLD_INITIAL_VALUES,
  CatalogMold,
} from "../../../models/CatalogMold";
import {
  deleteCatalogMold,
  upsertCatalogMold,
} from "../../../repositories/CatalogMoldRepository";
import { useCatalogMoldContext } from "../context/CatalogMoldContext";

type onModalHideProps = {
  entityId?: number;
  shouldUpdate?: boolean;
};

type Props = {
  mold: CatalogMold | null;
  showModal: "new" | "edit" | "delete" | "";
  onHide: ({ entityId, shouldUpdate }: onModalHideProps) => void;
  refreshData: Function;
};
export const MoldModal = ({ mold, showModal, onHide, refreshData }: Props) => {
  const { state } = useCatalogMoldContext();

  const [notificationMessage, setNotificationMessage] = useState("");
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [existingMold, setExistingMold] = useState("");
  const [title, setTitle] = useState("");

  const {
    control,
    getValues,
    handleSubmit,
    reset,
    formState: { dirtyFields },
  } = useForm<CatalogMold>({
    defaultValues: CATALOG_MOLD_INITIAL_VALUES,
  });

  const isFormDirty =
    !(Object.keys(dirtyFields).length === 0) || showModal == "new";

  const onSubmit: SubmitHandler<CatalogMold> = async (data: CatalogMold) => {
    setIsSubmitLoading(true);
    const molds: CatalogMold[] = state.molds || [];
    if (showModal === "delete") {
      setIsSubmitLoading(true);
      const resp = await deleteCatalogMold(mold?.MoldId ?? null);
      if (resp.ok) {
        await refreshData();
        onHide({ shouldUpdate: true });
        setNotificationMessage("The mold was deleted successfully");
      } else {
        setError(resp.message);
      }
      setIsSubmitLoading(false);
    } else {
      if (
        molds.some(
          (mold) =>
            mold.MoldName === data.MoldName && mold.MoldId !== data.MoldId,
        )
      ) {
        setExistingMold(data.MoldName);
        setConfirmOpen(true);
      } else {
        await performUpsert(data);
      }
    }
  };

  const performUpsert = async (data: CatalogMold) => {
    const resp = await upsertCatalogMold(data);
    if (resp.ok) {
      const entityId = get(resp, "data.returnValue", 0);
      await refreshData();
      setNotificationMessage("The mold was saved successfully");
      onHide({ entityId: entityId, shouldUpdate: true });
    } else {
      setError(resp.message);
      onHide({ shouldUpdate: false });
    }
    setIsSubmitLoading(false);
  };

  useEffect(() => {
    if (showModal != "") {
      setTitle(showModal.toUpperCase());
      reset(mold || CATALOG_MOLD_INITIAL_VALUES);
    }
  }, [showModal]);

  return (
    <>
      <ConfirmModal
        show={confirmOpen}
        onHide={(proceed: boolean) => {
          if (proceed) {
            performUpsert(getValues());
            onHide({ shouldUpdate: true });
          }
          setConfirmOpen(false);
          setIsSubmitLoading(false);
        }}
        existingName={existingMold}
        label="Mold"
      />
      <MesfModal
        title={`${title} MOLD`}
        open={showModal != ""}
        handleClose={() => onHide({ shouldUpdate: false })}
        id="mold-modal"
        maxWidth="lg"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <MesfModal.Content dividers style={{ padding: "15px 30px" }}>
            <Grid container spacing={1}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="MoldName"
                  control={control}
                  rules={{
                    required: "Name is required",
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label="Name"
                      value={field.value}
                      onChange={field.onChange}
                      variant="outlined"
                      fullWidth
                      margin="dense"
                      autoComplete="off"
                      error={!!error}
                      helperText={error?.message}
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="MoldSizeId"
                  control={control}
                  rules={{ required: "Mold Size is required" }}
                  render={({ field, fieldState: { error } }) => (
                    <MoldSizeAutocomplete
                      label="Mold Size"
                      value={field.value}
                      onChange={field.onChange}
                      fieldError={error}
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
              <Grid item sm={12} xs={12}>
                <Controller
                  name="Enabled"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          color="primary"
                          onChange={field.onChange}
                        />
                      }
                      label="Enabled"
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </MesfModal.Content>
          <MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
            <FooterModalButtons
              isSubmitLoading={isSubmitLoading}
              onHide={() => onHide({ shouldUpdate: false })}
              buttonLabel={showModal === "delete" ? "Delete" : "Save"}
              buttonColor={showModal === "delete" ? "secondary" : "primary"}
              disabled={!isFormDirty && showModal !== "delete"}
            />
          </MesfModal.Actions>
        </form>
      </MesfModal>
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
      <ErrorModal
        error={error}
        onHide={() => setError("")}
        title="Error with Mold"
      />
    </>
  );
};
